import { api, getApiBaseUrl } from './api';

export interface AiAssistantStatus {
  configured: boolean;
  chatModel: string;
  realtimeModel: string;
  agentFramework?: boolean;
  liveReadTools?: boolean;
}

export interface AiAssistantChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AiAssistantCoverageBrief {
  requestId: number;
  employeeName: string;
  leaveType: string;
  halfDay?: string | null;
  truncated: boolean;
  days: Array<{
    label: string;
    weekday: string;
    shift: string;
    schedules: Array<{
      name: string;
      usual: string;
      weeks: Array<{ label: string; people: number }>;
      onRota: number;
      available: number;
      approved: string[];
      pending: string[];
      free: string[];
    }>;
  }>;
}

export interface AiAssistantHolidayAction {
  kind: 'holiday.approve' | 'holiday.deny';
  label: string;
  requestId: number;
  summary: string;
  requiresReason: boolean;
  showCoverage?: boolean;
}

export interface AiAssistantAnswerFacts {
  outcome: string;
  period?: string | null;
  timezone?: string | null;
  asOf?: string;
  totals?: Record<string, number | string | null>;
}

export interface AiAssistantActionProposal {
  id: number;
  actionId: string;
  title: string;
  status: string;
  summary: string;
  affectedCount: number;
  warnings: string[];
  expiresAt: string;
  requiresReason: boolean;
  secondApprover: boolean;
  receipt?: string | null;
  executionState?: 'waiting' | 'pending' | 'unresolved' | 'executed' | 'not_executed';
}

export interface MissedClockOutRow {
  attendanceId: number;
  employeeId: number;
  employeeName: string;
  locationName: string;
  clockIn: string;
}

export interface MissedClockOutPage {
  total: number;
  page: number;
  pageSize: number;
  viewPath: string;
  items: MissedClockOutRow[];
}

export interface AiAssistantChatResponse {
  text: string;
  chatId: number;
  title: string;
  actions?: AiAssistantHolidayAction[];
  actionsTruncated?: boolean;
  proposals?: AiAssistantActionProposal[];
  facts?: AiAssistantAnswerFacts;
}

async function downloadBlob(path: string, format: 'xlsx' | 'pdf'): Promise<void> {
  const response = await api.get(path, {
    params: { format },
    responseType: 'blob',
  });
  const disposition = String(response.headers['content-disposition'] || '');
  const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
  const filename = match?.[1] || `chat.${format}`;
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const aiAssistantApi = {
  status: async (): Promise<AiAssistantStatus> => {
    const response = await api.get<AiAssistantStatus>('/ai-assistant/status');
    return response.data;
  },

  chat: async (
    messages: AiAssistantChatMessage[],
    chatId?: number | null,
  ): Promise<AiAssistantChatResponse> => {
    const response = await api.post<AiAssistantChatResponse>('/ai-assistant/chat', {
      messages,
      chatId: chatId ?? undefined,
    });
    return response.data;
  },

  missedClockOuts: async (page = 1, pageSize = 20): Promise<MissedClockOutPage> => {
    const response = await api.get<MissedClockOutPage>('/ai-assistant/missed-clock-outs', {
      params: { page, pageSize },
    });
    return response.data;
  },

  chats: async (): Promise<Array<{ id: number; title: string; createdAt: string; updatedAt: string }>> => {
    const response = await api.get('/ai-assistant/chats');
    return response.data;
  },

  chatThread: async (id: number): Promise<{
    id: number;
    title: string;
    messages: Array<{ id: number; role: string; content: string; createdAt: string }>;
  }> => {
    const response = await api.get(`/ai-assistant/chats/${id}`);
    return response.data;
  },

  deleteChat: async (id: number): Promise<void> => {
    await api.delete(`/ai-assistant/admin/chats/${id}`);
  },

  adminUsers: async (): Promise<Array<{ employeeId: number; name: string; chatCount: number; lastConversationAt: string }>> => {
    const response = await api.get('/ai-assistant/admin/users');
    return response.data;
  },

  adminUserChats: async (employeeId: number): Promise<Array<{ id: number; title: string; createdAt: string; updatedAt: string }>> => {
    const response = await api.get(`/ai-assistant/admin/users/${employeeId}/chats`);
    return response.data;
  },

  adminChat: async (id: number): Promise<{
    id: number;
    title: string;
    messages: Array<{ id: number; role: string; content: string; createdAt: string }>;
  }> => {
    const response = await api.get(`/ai-assistant/admin/chats/${id}`);
    return response.data;
  },

  renameChat: async (id: number, title: string): Promise<{ id: number; title: string }> => {
    const response = await api.patch<{ id: number; title: string }>(`/ai-assistant/chats/${id}`, { title });
    return response.data;
  },

  downloadChat: async (id: number, format: 'xlsx' | 'pdf'): Promise<void> => {
    await downloadBlob(`/ai-assistant/chats/${id}/export`, format);
  },

  downloadAdminChat: async (id: number, format: 'xlsx' | 'pdf'): Promise<void> => {
    await downloadBlob(`/ai-assistant/admin/chats/${id}/export`, format);
  },

  recordHolidayOutcome: async (
    chatId: number,
    requestId: number,
    outcome: 'approved' | 'denied',
  ): Promise<{ chatId: number; title: string; text: string }> => {
    const response = await api.post<{ chatId: number; title: string; text: string }>(
      `/ai-assistant/chats/${chatId}/holiday-outcome`,
      { requestId, outcome },
    );
    return response.data;
  },

  appendChat: async (chatId: number | null, role: 'user' | 'assistant', content: string): Promise<{ chatId: number; title: string }> => {
    const response = await api.post<{ chatId: number; title: string }>('/ai-assistant/chats/entries', {
      chatId,
      role,
      content,
    });
    return response.data;
  },

  actionInbox: async (): Promise<AiAssistantActionProposal[]> => {
    const response = await api.get<AiAssistantActionProposal[]>('/ai-assistant/actions/inbox');
    return response.data;
  },

  getProposal: async (proposalId: number): Promise<AiAssistantActionProposal> => {
    const response = await api.get<AiAssistantActionProposal>(`/ai-assistant/actions/${proposalId}`);
    return response.data;
  },

  listUnresolved: async (): Promise<AiAssistantActionProposal[]> => {
    const response = await api.get<AiAssistantActionProposal[]>('/ai-assistant/actions/unresolved');
    return response.data;
  },

  openExecutions: async (): Promise<Array<{
    proposalId: number;
    actionId: string;
    title: string;
    status: string;
    actorEmployeeId: number;
    affectedEmployeeId?: number | null;
    affectedEmployeeName?: string | null;
    parametersJson: string;
    attempts: Array<{ executionKey: string; status: string; error?: string | null; startedAt: string; closedAt?: string | null }>;
  }>> => {
    const response = await api.get('/ai-assistant/admin/executions');
    return response.data;
  },

  confirmAction: async (
    proposalId: number,
    body: { reason?: string; secondApproverId?: number; idempotencyKey?: string; chatId?: number | null },
  ): Promise<{ ok: boolean; receipt?: string; error?: string }> => {
    const response = await api.post(`/ai-assistant/actions/${proposalId}/confirm`, body);
    return response.data;
  },

  rejectAction: async (proposalId: number): Promise<{ ok: boolean; error?: string }> => {
    const response = await api.post(`/ai-assistant/actions/${proposalId}/reject`);
    return response.data;
  },

  holidayCoverage: async (requestId: number): Promise<AiAssistantCoverageBrief> => {
    const response = await api.get<AiAssistantCoverageBrief>(
      `/ai-assistant/holiday-requests/${requestId}/coverage`,
    );
    return response.data;
  },

  executeTool: async (
    name: string,
    args?: Record<string, unknown>,
  ): Promise<Record<string, unknown>> => {
    const response = await api.post('/ai-assistant/tools/execute', {
      name,
      arguments: args ? JSON.stringify(args) : undefined,
    });
    return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
  },

  createRealtimeSession: async (sdp: string): Promise<string> => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${getApiBaseUrl()}/ai-assistant/realtime/session`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/sdp',
      },
      body: sdp,
    });
    const text = await response.text();
    if (!response.ok) {
      try {
        const err = JSON.parse(text) as { message?: string };
        throw new Error(err.message || text || `Realtime session failed (${response.status})`);
      } catch (e) {
        if (e instanceof Error && e.message !== text) throw e;
        throw new Error(text || `Realtime session failed (${response.status})`);
      }
    }
    return text;
  },
};
